import React, {useState} from "react"
import {isEqual} from "lodash"

import GenericModal from "@irati/ui/src/GenericModal"
import modalStory, {anyStoriesOpen} from "@irati/utils/modalStory"
import apps, {RECOGNITION} from "@irati-web/app/src/routeConfig"
import {injectIntl} from "@irati/utils/lang"
import tracking from "@irati-tracking/recognition/recognition"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import {GroupCelebrationSource, GroupCelebration, CreateGroupCelebrationInput} from "@irati/types"
import {CancelConfirm} from "@irati-web/recognition/src/components/Popup/shared"
import History, {appOrContext} from "@irati/utils/history"
import Create from "./Create"
import AddMessage from "./AddMessage"
import SuccessMessage from "./SuccessMessage"
import CancelMessageConfirm from "./CancelMessageConfirm"

export const CREATE = "CREATE"
export const ADD_MESSAGE = "ADD_MESSAGE"
export const SUCCESS_MESSAGE = "SUCCESS_MESSAGE"
export const CANCEL_CONFIRM = "CANCEL_CONFIRM"
export const CANCEL_MESSAGE_CONFIRM = "CANCEL_MESSAGE_CONFIRM"

const modals = [CREATE, CANCEL_CONFIRM, CANCEL_MESSAGE_CONFIRM, ADD_MESSAGE, SUCCESS_MESSAGE]

export const EMPTY_GROUP_CELEBRATION: CreateGroupCelebrationInput = {
  collaboratorIds: [],
  greetingId: "",
  originalTimezone: "",
  receiverId: "",
  rewardAmount: 0,
  scheduledAt: "",
  source: GroupCelebrationSource.Perkbox
}
interface Props extends InjectIntlProps {
  onHide?: () => void
}

const CreateGroupCelebrationPopup: React.FC<Props> = (props) => {
  const {onHide} = props
  const [groupCelebration, setGroupCelebration] = useState(EMPTY_GROUP_CELEBRATION)
  const [scheduledGroupCelebration, setScheduledGroupCelebration] = useState<GroupCelebration>()
  const [show, setShow] = useState(modalStory(modals, CREATE))

  const shouldClearRecognition = () => !anyStoriesOpen(show)
  const showModal = (modalToShow = CREATE) => {
    const newEntity = shouldClearRecognition() ? EMPTY_GROUP_CELEBRATION : groupCelebration
    setGroupCelebration(newEntity)
    setShow(modalStory(modals, modalToShow, show))
  }
  const hide = () => {
    sendCancelEvent()
    setShow(modalStory(modals, null))
    History.push(appOrContext({to: "/polls", app: apps[RECOGNITION]}, {}))
    onHide?.()
  }
  const sendCancelEvent = () => {
    if (show.CREATE || show.CANCEL_CONFIRM) {
      tracking.recognitionCtaClick({
        source: "greeting_card_form",
        type: "greeting_card_cancel"
      })
    } else if (show.SUCCESS_MESSAGE || show.CANCEL_MESSAGE_CONFIRM) {
      tracking.recognitionCtaClick({
        source: "confirmation_modal",
        type: "greeting_card_cancel"
      })
    }
  }
  const cancelConfirm = () => {
    if (isEqual(groupCelebration, EMPTY_GROUP_CELEBRATION)) {
      return hide()
    }
    showModal(CANCEL_CONFIRM)
  }
  const onRecognitionChange = (newGroupCelebration: CreateGroupCelebrationInput) => {
    setGroupCelebration(newGroupCelebration)
  }
  const onRecognitionCreated = (newGroupCelebration: GroupCelebration, pointsPerUser = 0) => {
    tracking.launchGroupCelebration(newGroupCelebration, pointsPerUser)
    setScheduledGroupCelebration(newGroupCelebration)
    showModal(ADD_MESSAGE)
  }
  const onMessageSent = () => {
    tracking.launchGroupCelebration(
      scheduledGroupCelebration as GroupCelebration,
      scheduledGroupCelebration?.rewardAmount as number,
      "greeting_card_add_message"
    )
    showModal(SUCCESS_MESSAGE)
  }
  const onMessageSkip = () => {
    tracking.recognitionCtaClick({
      source: "confirmation_modal",
      type: "greeting_card_skip"
    })
    showModal(CANCEL_MESSAGE_CONFIRM)
  }
  return (
    <>
      <GenericModal show={show[CREATE]} onHide={cancelConfirm} customWidth="650" hideActions>
        <Create
          onRecognitionCreated={onRecognitionCreated}
          onRecognitionChange={onRecognitionChange}
          scheduledGroupCelebration={groupCelebration}
        />
      </GenericModal>
      <GenericModal show={show[ADD_MESSAGE]} customWidth="650" onHide={hide} hideActions>
        <AddMessage scheduledGroupCelebration={scheduledGroupCelebration!} onSkip={onMessageSkip} onMessageSent={onMessageSent} />
      </GenericModal>
      <GenericModal show={show[SUCCESS_MESSAGE]} small onHide={hide} hideActions>
        <SuccessMessage scheduledGroupCelebration={scheduledGroupCelebration!} onHide={hide} />
      </GenericModal>
      <GenericModal show={show[CANCEL_MESSAGE_CONFIRM]} critical hideActions>
        <CancelMessageConfirm previous={show.previous} selectScreen={showModal} onHide={hide} />
      </GenericModal>
      <GenericModal show={show[CANCEL_CONFIRM]} critical hideActions>
        <CancelConfirm previous={show.previous} selectScreen={showModal} onHide={hide} />
      </GenericModal>
    </>
  )
}

export default injectIntl(CreateGroupCelebrationPopup)
